import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * The brand's menu on a phone.
 *
 * The desktop menu opens its submenus on hover, which does not exist on a
 * touch screen: tapping a parent followed its link instead of opening what
 * was underneath, so every page in a submenu was unreachable. And a row of
 * links has nowhere to go at 360px wide — they wrapped into a stack that
 * pushed the page down before it began.
 *
 * So the bar collapses to a button, and the menu becomes a panel that slides
 * in. Submenus expand in place rather than flying out: there is no room
 * beside anything, and a nested list is how every phone menu works.
 *
 * Plain elements and inline styles like the rest of the public frame — this
 * runs on the brand's own domain and takes their colours, not ours.
 */

function MobileBranch({ link, style, color, depth }) {
  const [open, setOpen] = useState(false);
  const children = (link.children || []).filter((c) => c.label && c.url);
  const hasChildren = children.length > 0;

  const rowStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    width: '100%',
    padding: '12px 0',
    color,
    textDecoration: 'none',
    fontSize: style.fontSize + 1,
    fontWeight: depth === 0 ? 600 : 500,
    textTransform: style.uppercase ? 'uppercase' : 'none',
    background: 'none',
    border: 'none',
    font: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
    // Comfortably past the 44px anyone can reliably hit with a thumb.
    minHeight: 44,
  };

  return (
    <li style={{ listStyle: 'none', borderTop: depth === 0 ? '1px solid rgba(127,127,127,0.18)' : 'none' }}>
      <div style={{ display: 'flex', alignItems: 'center', paddingLeft: depth * 14 }}>
        {/* The label still navigates when it has somewhere to go. Only the
            chevron opens the submenu, so a parent that is also a real page
            stays reachable — the usual failure of phone menus. */}
        {link.url && link.url !== '#' ? (
          <Link to={link.url} style={{ ...rowStyle, flex: 1 }}>{link.label}</Link>
        ) : (
          <button type='button' onClick={() => setOpen((v) => !v)} style={{ ...rowStyle, flex: 1 }}>
            {link.label}
          </button>
        )}

        {hasChildren && (
          <button
            type='button'
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? `Hide ${link.label} menu` : `Show ${link.label} menu`}
            aria-expanded={open}
            style={{
              background: 'none', border: 'none', color, cursor: 'pointer',
              padding: '0 6px', minWidth: 44, minHeight: 44, fontSize: 12,
              opacity: 0.75, flexShrink: 0,
            }}
          >
            <span style={{
              display: 'inline-block',
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform 140ms ease',
            }}
            >
              ▼
            </span>
          </button>
        )}
      </div>

      {hasChildren && open && (
        <ul style={{ margin: 0, padding: 0 }}>
          {children.map((child, i) => (
            <MobileBranch key={i} link={child} style={style} color={color} depth={depth + 1} />
          ))}
        </ul>
      )}
    </li>
  );
}

export default function SiteNavMobile({ links, style, color, background, extras, themeButton }) {
  const [open, setOpen] = useState(false);
  const items = (links || []).filter((l) => l.label && l.url);

  // A menu that stays open behind a page change is a menu covering the page
  // you just asked for.
  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    window.addEventListener('popstate', close);
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    // The page behind must not scroll while the panel is over it.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('popstate', close);
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (items.length === 0 && !extras?.length) return null;

  return (
    <>
      <button
        type='button'
        onClick={() => setOpen(true)}
        aria-label='Open menu'
        aria-expanded={open}
        style={{
          background: 'none',
          border: `1px solid ${color}`,
          color,
          opacity: 0.8,
          borderRadius: 8,
          width: 40,
          height: 40,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          fontSize: 16,
          lineHeight: 1,
        }}
      >
        ☰
      </button>

      {open && (
        <>
          <div
            onClick={() => setOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 90 }}
          />
          <div
            role='dialog'
            aria-label='Menu'
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              width: 'min(340px, 86vw)',
              background,
              color,
              zIndex: 91,
              padding: '16px 20px 28px',
              overflowY: 'auto',
              boxShadow: '-16px 0 40px rgba(0,0,0,0.22)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
              <button
                type='button'
                onClick={() => setOpen(false)}
                aria-label='Close menu'
                style={{
                  background: 'none', border: 'none', color, cursor: 'pointer',
                  fontSize: 22, lineHeight: 1, minWidth: 44, minHeight: 44, opacity: 0.8,
                }}
              >
                ×
              </button>
            </div>

            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {items.map((link, i) => (
                <MobileBranch key={i} link={link} style={style} color={color} depth={0} />
              ))}
            </ul>

            {(extras?.length > 0 || themeButton) && (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 12,
                  marginTop: 22,
                  paddingTop: 18,
                  borderTop: '1px solid rgba(127,127,127,0.18)',
                }}
              >
                {extras}
                {themeButton}
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}
